import type { MenuItemTagV2 } from "@/models/menu-item-v2";

export type DietaryType = "VEG" | "NON_VEG";

export function getDietaryType(tags: MenuItemTagV2[] | undefined): DietaryType | null {
  const normalized = (tags ?? []).map((t) => String(t?.tag_code ?? "").trim().toUpperCase());
  if (normalized.includes("VEG")) return "VEG";
  if (normalized.includes("NON_VEG")) return "NON_VEG";
  return null;
}

/** Primary food identity codes — not shown as secondary dietary chips. */
export const PRIMARY_FOOD_TYPE_CODES = new Set([
  "VEG",
  "NON_VEG",
  "VEGAN",
  "VEGETARIAN",
  "PESCATARIAN",
  "EGGITARIAN",
]);

export type PrimaryFoodType =
  | "VEG"
  | "NON_VEG"
  | "VEGAN"
  | "VEGETARIAN"
  | "PESCATARIAN"
  | "EGGITARIAN";

const PRIMARY_FOOD_TYPE_PRIORITY: PrimaryFoodType[] = [
  "VEGAN",
  "VEGETARIAN",
  "VEG",
  "PESCATARIAN",
  "EGGITARIAN",
  "NON_VEG",
];

const PRIMARY_FOOD_TYPE_SHORT_LABELS: Record<PrimaryFoodType, string> = {
  VEG: "Veg",
  NON_VEG: "Non-Veg",
  VEGAN: "Vegan",
  VEGETARIAN: "Veg",
  PESCATARIAN: "Pescatarian",
  EGGITARIAN: "Eggitarian",
};

const PRIMARY_FOOD_TYPE_ARIA_LABELS: Record<PrimaryFoodType, string> = {
  VEG: "Vegetarian",
  NON_VEG: "Non-vegetarian",
  VEGAN: "Vegan",
  VEGETARIAN: "Vegetarian",
  PESCATARIAN: "Pescatarian",
  EGGITARIAN: "Eggitarian",
};

export function getPrimaryFoodType(tags: MenuItemTagV2[] | undefined): PrimaryFoodType | null {
  const codes = new Set((tags ?? []).map((t) => String(t?.tag_code ?? "").trim().toUpperCase()));
  for (const code of PRIMARY_FOOD_TYPE_PRIORITY) {
    if (codes.has(code)) return code;
  }
  return null;
}

/** Short UI label for primary food type (e.g. Veg, Non-Veg). */
export function getPrimaryFoodTypeLabel(type: PrimaryFoodType): string {
  return PRIMARY_FOOD_TYPE_SHORT_LABELS[type];
}

/** Accessible full label for screen readers. */
export function getPrimaryFoodTypeAriaLabel(type: PrimaryFoodType): string {
  return PRIMARY_FOOD_TYPE_ARIA_LABELS[type];
}

